import { TextInput, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import type { DraftSet } from './state/loggerDraft';
import type { useTheme } from './theme';
import type { useFieldFocusReporter } from './useFieldFocus';
import { useSelectAllOnFocus } from './useSelectAllOnFocus';
import type { makeFocusPanelInputsStyles } from './focusPanelInputsStyles';
import type { EditableSetField } from './FocusPanelInputs';
import { AssistSignMark } from './AssistSignMark';

// The weight input of the focus panel, extracted from FocusPanelInputs (500
// line budget). On a bodyweight row it is the EXTRA field: magnitude-only text,
// the sign on `extraNegative`. The «Assisted» chip (#213) lives in the chip
// row (FocusPanelChipRow): a minus nobody knows to type, and that the decimal
// pad may not even offer, is not a control. A typed minus still
// works: it is stripped into the flag, exactly as before.
export function ExtraLoadField({
  set: s,
  isBodyweight,
  invalid,
  onChange,
  fieldFocus,
  onFocusChange,
  onWeightFocus,
  styles,
  theme,
}: {
  set: DraftSet;
  isBodyweight: boolean;
  invalid: boolean;
  onChange: (patch: Partial<Pick<DraftSet, EditableSetField>>) => void;
  fieldFocus: ReturnType<typeof useFieldFocusReporter>;
  /** Mirrors focus/blur to the panel (it docks the load bar while focused). */
  onFocusChange: (focused: boolean) => void;
  onWeightFocus?: () => void;
  styles: ReturnType<typeof makeFocusPanelInputsStyles>;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  // Tap a populated field → select the whole number so you type over it.
  const weightSel = useSelectAllOnFocus(s.weight);

  // A typed minus turns the field amber (SetRow.onChangeWeight parity).
  const onChangeWeight = (text: string) => {
    if (!isBodyweight) {
      onChange({ weight: text });
      return;
    }
    const hasMinus = /[-−]/.test(text);
    const magnitude = text.replace(/[-−]/g, '').trim();
    if (hasMinus) onChange({ weight: magnitude, extraNegative: true });
    // Emptying the field keeps the sign (an assisted exercise stays assisted).
    else if (magnitude === '') onChange({ weight: '' });
    else onChange({ weight: magnitude });
  };
  const isAssist =
    isBodyweight && (s.extraNegative || /^\s*[-−]/.test(s.weight));

  return (
    <View style={styles.inputWrap}>
      <TextInput
        ref={weightSel.ref}
        // Always named: the visual label sits in another node, so a reader
        // landing on the field must still hear what it is.
        accessibilityLabel={
          !isBodyweight
            ? undefined
            : isAssist
              ? t('mobile.logger.setExtraAssistA11y', {
                  defaultValue: 'Extra, assisted bodyweight load',
                })
              : t('mobile.logger.setExtra', { defaultValue: 'EXTRA' })
        }
        style={[
          styles.input,
          isAssist && styles.inputNegative,
          isAssist && styles.inputAssist,
          invalid && styles.inputInvalid,
        ]}
        keyboardType="decimal-pad"
        inputMode="decimal"
        placeholder={isAssist ? '' : '\u2014'}
        placeholderTextColor={theme.palette.ink3}
        value={s.weight}
        onChangeText={onChangeWeight}
        selectTextOnFocus
        onFocus={() => {
          onFocusChange(true);
          weightSel.onFocus();
          fieldFocus.onFocus('weight');
          onWeightFocus?.();
        }}
        onBlur={() => {
          onFocusChange(false);
          fieldFocus.onBlur('weight');
        }}
        maxLength={6}
      />
      {isAssist ? <AssistSignMark theme={theme} /> : null}
    </View>
  );
}
